/**
 * The book's results, for the person who set it.
 *
 * A learner's report ends with the eight questions *they* should look at again.
 * A teacher cannot use thirty of those. What a teacher can use is the two questions
 * the class could not do and the names of the learners who missed them, because
 * that is tomorrow's lesson and everything else in the book went fine.
 *
 * Three decisions shape this screen.
 *
 *  - **It leads with the questions, not the scores.** A class average is a
 *    sentence about the class; a question twelve learners missed is a sentence
 *    about the *book*, and it is the one a teacher can act on. So the questions
 *    come first, each with its own evidence, and the roster and the average sit
 *    below as context rather than as the headline.
 *  - **Names, not counts, beside every question.** "7 of 9 missed this" tells a
 *    teacher to re-teach it; "*Amina, Bilal, Chloé…* — Bilal wrote *Venus*" tells
 *    them what to say when they do. The answers travel with the names because the
 *    wrong answer is usually a shared misconception, and one look at five of them
 *    is worth more than a percentage.
 *  - **It never invents a failure.** A question nobody answered is in its own list
 *    under "nobody reached these", and a question one learner answered is shown but
 *    never called hard (see `#/lib/flashbookClassReport`). A teacher reading this
 *    during a lesson is deciding what to do with the next forty minutes; a
 *    mislabelled "hardest question" costs them the lesson.
 *
 * Like the learner's report, it is printed on the book's own paper in the book's own
 * type — a teacher and their learner should be looking at the same book, not at two
 * applications.
 */
import {useMemo, useState} from 'react'
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  buildFlashBookClassReport,
  type ClassInsight,
  type ClassLearnerRow,
  type ClassPlay,
  type ClassQuestionRow,
  type ClassSkillRow,
} from '#/lib/flashbookClassReport'
import {
  directionStyle,
  directionTextStyle,
  type FlashBookDirection,
} from '#/lib/flashbookDirection'
import {useFlashbookClassPlaysQuery} from '#/state/queries/flashbook-class-plays'
import {
  FlashBookPlayThemeProvider,
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  usePlayPrimaryAction,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {
  useCopyStyle,
  useReportType,
} from '#/components/flashbooks/useReportChrome'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {type FlashBookPage, type FlashBookThemeTokens} from '#/types/flashbook'

/** How many names a question prints before it says "and N more". */
const NAMES_SHOWN = 5

/**
 * Whether an answer is worth printing.
 *
 * `'—'` is the app's own way of saying "there is nothing to show" (see
 * `#/lib/answerFormat`), so a card that printed it would put an em dash under
 * *Answer:* — which reads as a question with no right answer rather than as one the
 * report cannot render. Both the answer line and a learner's row drop it instead.
 */
function printable(answer: string): boolean {
  return !!answer.trim() && answer.trim() !== '—'
}

type Props = {
  bookTitle: string
  /** The book's AT URI, which is how the server finds its plays. */
  flashbookUri: string
  pages: FlashBookPage[]
  /**
   * The class's plays, supplied directly.
   *
   * A fixture and a test hand the plays in; a teacher leaves this undefined and the
   * screen asks the AppView. It is a prop rather than a second component so the
   * screen the browser measures is the screen that ships.
   */
  plays?: ClassPlay[]
  themeTokens: FlashBookThemeTokens
  design?: FlashBookDesign
  direction?: FlashBookDirection
  /**
   * Where the way out goes, and so what it is called.
   *
   * Two doors lead in and they must not come back through the same one. Reached
   * from the report of the teacher's own read, the way out is back to *that report*
   * — the screen they were reading a moment ago. Reached straight from the book's
   * card in the feed there is no such report, and the button has to say so: a
   * teacher told they can "go back to the book" who is then dropped into a report of
   * a read they never did has been told something untrue. Defaults to the report,
   * which is the entry that existed first.
   */
  returnTo?: 'report' | 'exit'
  onClose: () => void
}

export function FlashBookClassReport({
  themeTokens,
  design,
  direction,
  ...props
}: Props) {
  return (
    <FlashBookPlayThemeProvider
      tokens={themeTokens}
      design={design}
      direction={direction}>
      <ClassBody {...props} tok={themeTokens} direction={direction} />
    </FlashBookPlayThemeProvider>
  )
}

function ClassBody({
  bookTitle,
  flashbookUri,
  pages,
  plays,
  tok,
  direction,
  returnTo = 'report',
  onClose,
}: Omit<Props, 'themeTokens' | 'design'> & {tok: FlashBookThemeTokens}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const pagePad = useFlashBookPlayDesign().page.padding
  const stackGap = usePlayStackGap(18)
  const action = usePlayPrimaryAction(48)
  const rtl = direction === 'rtl'
  const mirrored = rtl ? directionStyle('rtl') : null

  /*
   * The plays, from the caller or from the app.
   *
   * The hook is always called — with no URI when the plays were handed in — so the
   * fixture and the shipping screen take the same path through React, and a
   * conditional hook cannot be introduced here later by accident.
   */
  const query = useFlashbookClassPlaysQuery(plays ? undefined : flashbookUri)
  const model = useMemo(
    () => buildFlashBookClassReport({pages, plays: plays ?? query.data ?? []}),
    [pages, plays, query.data],
  )

  const loading = !plays && query.isPending
  const failed = !plays && query.isError
  const {overview} = model

  return (
    <View
      testID="flashbook-class-report"
      style={[styles.root, {backgroundColor: tok.pageBg}]}>
      <ScrollView
        style={styles.flex}
        contentContainerStyle={[
          styles.content,
          {paddingHorizontal: pagePad, gap: stackGap},
        ]}
        showsVerticalScrollIndicator={false}>
        {/* ── Whose book, and whose class ─────────────────────────────────── */}
        <View style={styles.head}>
          <Text
            style={[
              type.caption,
              copy.text,
              {color: pal.muted},
              directionTextStyle(direction ?? 'ltr'),
            ]}>
            {bookTitle}
          </Text>
          <Text
            testID="flashbook-class-title"
            style={[type.display, copy.text, {color: pal.text}]}>
            {l`Class report`}
          </Text>
          <Text style={[type.body, copy.text, {color: pal.body}]}>
            {loading ? (
              <Trans>Gathering the class's work…</Trans>
            ) : failed ? (
              <Trans>
                The class's plays could not be loaded. Try again in a moment.
              </Trans>
            ) : model.empty ? (
              <Trans>
                No one has played this book yet. Results appear here as learners
                finish it.
              </Trans>
            ) : (
              <Plural
                value={overview.learners}
                one="One learner has played this book."
                other="# learners have played this book."
              />
            )}
          </Text>
        </View>

        {loading && (
          <View testID="flashbook-class-loading" style={styles.centered}>
            <ActivityIndicator color={tok.accent} />
          </View>
        )}

        {!loading && !failed && !model.empty && (
          <>
            {/* ── How the class stands ──────────────────────────────────────── */}
            <View style={[styles.tiles, mirrored]}>
              <Tile
                testID="flashbook-class-stat--learners"
                icon="users"
                value={`${overview.learners}`}
                label={l`Learners`}
              />
              <Tile
                testID="flashbook-class-stat--average"
                icon="award"
                value={`${overview.meanPct}%`}
                label={l`Class average`}
                tone={tok.accent}
              />
              <Tile
                testID="flashbook-class-stat--finished"
                icon="book-open"
                value={`${overview.finished}/${overview.learners}`}
                label={l`Finished`}
              />
              {overview.meanTimeMs > 0 && (
                <Tile
                  testID="flashbook-class-stat--plays"
                  icon="repeat"
                  value={`${overview.plays}`}
                  label={l`Plays`}
                />
              )}
            </View>

            <Distribution
              buckets={model.distribution}
              accent={tok.accent}
              mirrored={mirrored}
            />

            {/* ── What to teach ───────────────────────────────────────────── */}
            <View style={styles.section}>
              <SectionTitleText>{l`Questions to teach`}</SectionTitleText>
              <Text style={[type.caption, copy.text, {color: pal.muted}]}>
                <Trans>
                  Ranked by how many learners missed them, and each one names
                  who.
                </Trans>
              </Text>
              {model.hardest.length === 0 ? (
                <View
                  testID="flashbook-class-hardest--none"
                  style={[
                    styles.note,
                    {
                      borderColor: pal.paperHairline,
                      backgroundColor: pal.surfaceAlt,
                    },
                  ]}>
                  <Text style={[type.caption, copy.text, {color: pal.muted}]}>
                    <Trans>
                      No question was missed by enough learners to be worth a
                      lesson — either the class did well, or not enough of them
                      have played yet.
                    </Trans>
                  </Text>
                </View>
              ) : (
                model.hardest.map(question => (
                  <HardQuestionCard
                    key={question.blockId}
                    question={question}
                    accent={tok.accent}
                    positive={pal.positive}
                    negative={pal.negative}
                    warning={pal.warning}
                  />
                ))
              )}
            </View>

            {/* ── What the class never got to ─────────────────────────────── */}
            {model.unreached.length > 0 && (
              <View style={styles.section}>
                <SectionTitleText>{l`Nobody reached these`}</SectionTitleText>
                <Text
                  testID="flashbook-class-unreached"
                  style={[type.caption, copy.text, {color: pal.muted}]}>
                  <Plural
                    value={model.unreached.length}
                    one="One question was never answered by anyone — not a failure, a matter of time."
                    other="# questions were never answered by anyone — not a failure, a matter of time."
                  />
                </Text>
                {model.unreached.map(question => (
                  <View
                    key={question.blockId}
                    testID={`flashbook-class-unreached--${question.blockId}`}
                    style={[
                      styles.plainRow,
                      {
                        borderColor: pal.paperHairline,
                        backgroundColor: pal.surfaceAlt,
                      },
                      mirrored,
                    ]}>
                    <Feather name="clock" size={13} color={pal.muted} />
                    <Text
                      style={[
                        type.label,
                        copy.text,
                        {color: pal.body},
                        styles.flex,
                        directionTextStyle(question.direction),
                      ]}
                      numberOfLines={2}>
                      {question.prompt || question.label}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* ── How the class did, by kind of question ──────────────────── */}
            {model.skills.some(skill => skill.answered > 0) && (
              <View style={styles.section}>
                <SectionTitleText>{l`By kind of question`}</SectionTitleText>
                {model.skills.map(skill => (
                  <SkillRow
                    key={skill.type}
                    skill={skill}
                    accent={tok.accent}
                    track={pal.paperTrack}
                    negative={pal.negative}
                  />
                ))}
              </View>
            )}

            {/* ── Who needs what ─────────────────────────────────────────── */}
            <View style={styles.section}>
              <SectionTitleText>{l`The class`}</SectionTitleText>
              {model.learners.map(learner => (
                <LearnerRow
                  key={learner.did}
                  learner={learner}
                  pages={pages.length}
                  accent={tok.accent}
                  track={pal.paperTrack}
                />
              ))}
            </View>

            {/* ── What it adds up to ─────────────────────────────────────── */}
            {model.insights.length > 0 && (
              <View style={styles.section}>
                <SectionTitleText>{l`What stands out`}</SectionTitleText>
                {model.insights.map((insight, index) => (
                  <InsightRow
                    key={`${insight.kind}-${index}`}
                    insight={insight}
                    negative={pal.negative}
                    muted={pal.muted}
                  />
                ))}
              </View>
            )}
          </>
        )}

        <View style={[styles.actions, mirrored]}>
          <TouchableOpacity
            testID="flashbook-class-close"
            accessibilityRole="button"
            accessibilityHint={
              returnTo === 'exit'
                ? l`Closes the class report`
                : l`Closes the class report and returns to the book`
            }
            onPress={onClose}
            style={[
              styles.actionBtn,
              {backgroundColor: tok.accent},
              action.style,
            ]}>
            <Feather
              name={returnTo === 'exit' ? 'check' : 'arrow-left'}
              size={15}
              color={action.ink(true) ?? pal.onAccent}
            />
            <Text
              style={[
                type.label,
                copy.text,
                {color: action.ink(true) ?? pal.onAccent},
                action.labelStyle(true),
              ]}>
              {returnTo === 'exit' ? (
                <Trans>Done</Trans>
              ) : (
                <Trans>Back to the book</Trans>
              )}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  )
}

/** One number about the class, with room for a label under it. */
function Tile({
  testID,
  icon,
  value,
  label,
  tone,
}: {
  testID: string
  icon: string
  value: string
  label: string
  tone?: string
}) {
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  return (
    <View
      testID={testID}
      style={[
        styles.tile,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
      ]}>
      <Feather name={icon as never} size={13} color={tone ?? pal.muted} />
      <Text
        style={[type.title, copy.text, {color: tone ?? pal.text}]}
        numberOfLines={1}>
        {value}
      </Text>
      <Text style={[type.caption, copy.text, {color: pal.muted}]}>{label}</Text>
    </View>
  )
}

/**
 * How the scores are spread.
 *
 * A bar per band rather than an average, because two classes with the same mean can
 * be one class that all did fine and one that has half a lesson at either end — and
 * it is the second one a teacher needs to see before they plan.
 */
function Distribution({
  buckets,
  accent,
  mirrored,
}: {
  buckets: {from: number; to: number; learners: number}[]
  accent: string
  mirrored: ReturnType<typeof directionStyle> | null
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const most = Math.max(1, ...buckets.map(bucket => bucket.learners))

  return (
    <View
      testID="flashbook-class-distribution"
      style={[
        styles.chart,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
        mirrored,
      ]}>
      <Text style={[type.caption, copy.text, {color: pal.muted}]}>
        {l`How the scores are spread`}
      </Text>
      <View style={styles.bars}>
        {buckets.map(bucket => (
          <View
            key={bucket.from}
            testID={`flashbook-class-bucket--${bucket.from}`}
            style={styles.barColumn}>
            <Text style={[type.caption, copy.text, {color: pal.body}]}>
              {bucket.learners}
            </Text>
            <View
              style={[
                styles.barTrack,
                {backgroundColor: pal.paperTrack, height: 54},
              ]}>
              <View
                style={[
                  styles.barFill,
                  {
                    backgroundColor: accent,
                    height: Math.round((bucket.learners / most) * 54),
                  },
                ]}
              />
            </View>
            <Text style={[type.caption, copy.text, {color: pal.muted}]}>
              {`${bucket.from}–${bucket.to}%`}
            </Text>
          </View>
        ))}
      </View>
    </View>
  )
}

/**
 * One question worth teaching, with the people who missed it.
 *
 * The card is deliberately the same shape as the learner's own focus card — page,
 * question, the answer — because the same two people are looking at the same
 * question, and a teacher reading it aloud should be reading what the learner sees.
 * What is added is the count and the names.
 */
function HardQuestionCard({
  question,
  accent,
  positive,
  negative,
  warning,
}: {
  question: ClassQuestionRow
  accent: string
  positive: string
  negative: string
  warning: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const [expanded, setExpanded] = useExpanded()
  const shown = expanded
    ? question.missedBy
    : question.missedBy.slice(0, NAMES_SHOWN)
  const hidden = question.missedBy.length - shown.length
  const dir = directionTextStyle(question.direction)

  return (
    <View
      testID={`flashbook-class-question--${question.blockId}`}
      style={[
        styles.card,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
      ]}>
      <View style={styles.cardHead}>
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          {l`Page ${question.pageIndex + 1}`}
          {question.pageTitle ? ` · ${question.pageTitle}` : ''}
        </Text>
        <View style={[styles.countChip, {borderColor: pal.paperHairline}]}>
          <Text
            testID={`flashbook-class-missed--${question.blockId}`}
            style={[type.caption, copy.text, {color: negative}]}>
            <Trans>
              {question.wrong} of {question.answered} missed
            </Trans>
          </Text>
        </View>
      </View>

      <Text
        style={[type.question, copy.text, {color: pal.text}, dir]}
        numberOfLines={3}>
        {question.prompt || question.label}
      </Text>

      <View style={[styles.meterTrack, {backgroundColor: pal.paperTrack}]}>
        <View
          style={[
            styles.meterFill,
            {
              width: `${Math.round(question.wrongRate * 100)}%`,
              backgroundColor: question.wrongRate >= 0.75 ? negative : warning,
            },
          ]}
        />
      </View>

      {printable(question.correctAnswer) && (
        <Text style={[type.body, copy.text, {color: pal.body}, dir]}>
          {l`Answer: ${question.correctAnswer}`}
        </Text>
      )}

      <View style={styles.section}>
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          <Trans>Missed by</Trans>
        </Text>
        {shown.map(miss => (
          <View
            key={miss.did}
            testID={`flashbook-class-miss--${question.blockId}--${miss.did}`}
            style={styles.missRow}>
            <Feather
              name={miss.partial ? 'minus-circle' : 'x-circle'}
              size={12}
              color={miss.partial ? warning : negative}
            />
            <Text
              style={[type.label, copy.text, {color: pal.text}, styles.flex]}
              numberOfLines={1}>
              {miss.name}
            </Text>
            {/* What they wrote — the half of a class report a teacher actually
                teaches from, because a shared wrong answer is a shared idea. A
                half-right answer is named as one, and an answer the app cannot
                render is left off rather than printed as a dash. */}
            <Text
              style={[
                type.caption,
                copy.text,
                {color: miss.partial ? warning : pal.body},
              ]}
              numberOfLines={1}>
              {miss.partial
                ? l`part right`
                : printable(miss.answer)
                  ? miss.answer
                  : ''}
            </Text>
          </View>
        ))}
        {hidden > 0 && (
          <TouchableOpacity
            testID={`flashbook-class-missed-more--${question.blockId}`}
            accessibilityRole="button"
            onPress={() => setExpanded(true)}
            style={styles.moreBtn}>
            <Text style={[type.caption, copy.text, {color: accent}]}>
              <Plural value={hidden} one="and one more" other="and # more" />
            </Text>
          </TouchableOpacity>
        )}
        {question.correctBy.length > 0 && (
          <Text
            testID={`flashbook-class-correct--${question.blockId}`}
            style={[type.caption, copy.text, {color: positive}]}>
            <Trans>
              {question.correctBy.length} can explain it:{' '}
              {correctNames(question)}
            </Trans>
          </Text>
        )}
      </View>
    </View>
  )
}

/** The first few names of the learners who got a question right. */
function correctNames(question: ClassQuestionRow): string {
  const names = question.correctBy.slice(0, 3).map(ref => ref.name)
  const rest = question.correctBy.length - names.length
  return rest > 0 ? `${names.join(', ')} +${rest}` : names.join(', ')
}

/** One block type, as the class managed it. */
function SkillRow({
  skill,
  accent,
  track,
  negative,
}: {
  skill: ClassSkillRow
  accent: string
  track: string
  negative: string
}) {
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const anyWrong = skill.wrong > 0
  const ink = anyWrong ? negative : pal.body
  return (
    <View
      testID={`flashbook-class-skill--${skill.type}`}
      style={styles.skillRow}>
      <View style={styles.skillHead}>
        <Text style={[type.label, copy.text, {color: pal.text}, styles.flex]}>
          {skill.label}
        </Text>
        <Text style={[type.caption, copy.text, {color: ink}]}>
          {skill.answered > 0
            ? `${skill.correct}/${skill.answered}`
            : `${skill.exercises} not tried`}
        </Text>
      </View>
      <View style={[styles.meterTrack, {backgroundColor: track}]}>
        <View
          style={[
            styles.meterFill,
            {
              width: `${Math.round(skill.accuracy * 100)}%`,
              backgroundColor: anyWrong ? accent : pal.positive,
            },
          ]}
        />
      </View>
    </View>
  )
}

/** One learner, as they stand. */
function LearnerRow({
  learner,
  pages,
  accent,
  track,
}: {
  learner: ClassLearnerRow
  pages: number
  accent: string
  track: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const unfinished = pages > 0 && learner.pagesReached < pages

  return (
    <View
      testID={`flashbook-class-learner--${learner.did}`}
      style={[
        styles.learnerRow,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
      ]}>
      <View style={styles.learnerHead}>
        <Text
          style={[type.label, copy.text, {color: pal.text}, styles.flex]}
          numberOfLines={1}>
          {learner.name}
        </Text>
        <Text
          testID={`flashbook-class-learner-pct--${learner.did}`}
          style={[type.title, copy.text, {color: accent}]}>
          {learner.pct}%
        </Text>
      </View>

      <View style={[styles.meterTrack, {backgroundColor: track}]}>
        <View
          style={[
            styles.meterFill,
            {width: `${learner.pct}%`, backgroundColor: accent},
          ]}
        />
      </View>

      <Text style={[type.caption, copy.text, {color: pal.muted}]}>
        {learner.skipped > 0 ? (
          <Trans>
            {learner.correct} right · {learner.skipped} not answered
          </Trans>
        ) : (
          <Trans>
            {learner.correct} right of {learner.answered}
          </Trans>
        )}
        {unfinished
          ? ` · ${l`stopped at page ${learner.pagesReached}`}`
          : pages > 0
            ? ` · ${l`whole book`}`
            : ''}
        {learner.attempts > 1 ? ` · ${l`${learner.attempts} plays`}` : ''}
      </Text>
    </View>
  )
}

/** One sentence a teacher should take away, drawn in its own tone. */
function InsightRow({
  insight,
  negative,
  muted,
}: {
  insight: ClassInsight
  negative: string
  muted: string
}) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()

  const {icon, ink, text} = useMemo(() => {
    switch (insight.kind) {
      case 'hardest':
        return {
          icon: 'target',
          ink: negative,
          text: l`${insight.wrong} of ${insight.of} missed “${truncate(
            insight.question.prompt || insight.question.label,
          )}” on page ${insight.question.pageIndex + 1}.`,
        }
      case 'nobody':
        return {
          icon: 'alert-triangle',
          ink: negative,
          text: l`Not one learner got “${truncate(
            insight.question.prompt || insight.question.label,
          )}” right — start there.`,
        }
      case 'split':
        return {
          icon: 'git-branch',
          ink: muted,
          text: l`“${truncate(
            insight.question.prompt || insight.question.label,
          )}” split the class — ${Math.round(
            insight.wrongRate * 100,
          )}% missed it. Have the half who got it explain it.`,
        }
      case 'weakestType':
        return {
          icon: 'bar-chart-2',
          ink: negative,
          text: l`${insight.skill.label} cost the class the most: ${
            insight.skill.wrong
          } wrong of ${insight.skill.answered}.`,
        }
      case 'unreached':
        return {
          icon: 'clock',
          ink: muted,
          text: l`Nobody answered ${insight.count} of the book's ${
            insight.of
          } questions — the class did not get that far.`,
        }
      case 'spread':
        return {
          icon: 'users',
          ink: muted,
          text: l`${insight.top.name} scored ${insight.top.pct}% and ${
            insight.bottom.name
          } scored ${insight.bottom.pct}% — ${insight.gap} points apart.`,
        }
      case 'needsHelp':
        return {
          icon: 'life-buoy',
          ink: negative,
          text: l`${insight.learner.name} is at ${
            insight.learner.pct
          }% with ${insight.learner.missedBlockIds.length} questions open.`,
        }
    }
  }, [insight, l, negative, muted])

  return (
    <View
      testID={`flashbook-class-insight--${insight.kind}`}
      style={[styles.insightRow, {borderColor: pal.paperHairline}]}>
      <Feather name={icon as never} size={14} color={ink} />
      <Text style={[type.body, copy.text, {color: pal.body}, styles.flex]}>
        {text}
      </Text>
    </View>
  )
}

function SectionTitleText({children}: {children: React.ReactNode}) {
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  return (
    <Text
      style={[type.title, copy.text, {color: pal.text}, styles.sectionTitle]}>
      {children}
    </Text>
  )
}

/** Local expansion state, so a card with a long roster does not need a parent. */
function useExpanded(): [boolean, (next: boolean) => void] {
  const [expanded, setExpanded] = useState(false)
  return [expanded, setExpanded]
}

function truncate(text: string, max = 46): string {
  const clean = text.trim()
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean
}

const styles = StyleSheet.create({
  root: {flex: 1},
  flex: {flex: 1},
  content: {paddingTop: 22, paddingBottom: 40},
  head: {gap: 6},
  centered: {paddingVertical: 28, alignItems: 'center'},
  tiles: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
  tile: {
    flexGrow: 1,
    flexBasis: '45%',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 3,
  },
  chart: {borderWidth: 1, borderRadius: 12, padding: 12, gap: 10},
  bars: {flexDirection: 'row', alignItems: 'flex-end', gap: 10},
  barColumn: {flex: 1, alignItems: 'center', gap: 4},
  barTrack: {
    width: '100%',
    justifyContent: 'flex-end',
    borderRadius: 4,
    overflow: 'hidden',
  },
  barFill: {width: '100%', borderRadius: 4},
  section: {gap: 8},
  sectionTitle: {marginBottom: 2},
  card: {borderWidth: 1, borderRadius: 12, padding: 13, gap: 9},
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  countChip: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  meterTrack: {height: 5, borderRadius: 3, overflow: 'hidden'},
  meterFill: {height: 5, borderRadius: 3},
  missRow: {flexDirection: 'row', alignItems: 'center', gap: 7},
  moreBtn: {paddingVertical: 3},
  plainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 11,
    paddingVertical: 9,
  },
  skillRow: {gap: 5},
  skillHead: {flexDirection: 'row', alignItems: 'center', gap: 8},
  learnerRow: {borderWidth: 1, borderRadius: 12, padding: 12, gap: 6},
  learnerHead: {flexDirection: 'row', alignItems: 'center', gap: 8},
  insightRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    borderTopWidth: 1,
    paddingTop: 9,
  },
  note: {borderWidth: 1, borderRadius: 12, padding: 12},
  actions: {gap: 8, marginTop: 4},
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 14,
  },
})
